/**
 * @description Custom hook that provides functionality to copy text or objects to the clipboard.
 */
import { useState, useCallback } from 'react';

export interface UseCopyToClipboardOptions {
  resetAfter?: number; // Time in milliseconds to reset the copied state
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export interface UseCopyToClipboardReturn {
  copied: boolean;
  copy: (data: string | object) => unknown;
  reset: () => void;
}

export function useCopyToClipboard(options: UseCopyToClipboardOptions = {}): UseCopyToClipboardReturn {
  const {
    resetAfter = 2000,
    onSuccess,
    onError
  } = options;

  const [copied, setCopied] = useState(false);

  const copy = useCallback(async (data: string | object) => {
    try {
      const textToCopy = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
      
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      setCopied(true);
      onSuccess?.();

      // Reset copied state after specified time
      if (resetAfter > 0) {
        setTimeout(() => setCopied(false), resetAfter);
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Failed to copy to clipboard');
      onError?.(err);
      console.error('Copy to clipboard failed:', err);
    }
  }, [resetAfter, onSuccess, onError]);

  const reset = useCallback(() => {
    setCopied(false);
  }, []);

  return {
    copied,
    copy,
    reset
  };
}